function attendanceCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
}

function formatDisplayDate(isoDate) {
  const date = new Date(`${isoDate}T12:00:00`);
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function renderTimelineAxis(timeline) {
  if (!timeline?.axis_ticks?.length) {
    return '';
  }

  const ticks = timeline.axis_ticks.map((tick) => `<span>${tick.label}</span>`).join('');
  return `<div class="attendance-timeline-axis">${ticks}</div>`;
}

function renderSessionTimelineRows(sessions, timeline) {
  if (!sessions?.length || !timeline?.segments?.length) {
    return '';
  }

  return sessions.map((session, index) => {
    const segment = timeline.segments[index];
    if (!segment) {
      return '<div class="attendance-timeline-row"></div>';
    }

    const cssClass = segment.complete ? 'complete' : 'incomplete';
    const labelLeft = segment.start_percent + (segment.width_percent / 2);
    const icon = segment.complete
      ? '<i class="fas fa-check-circle"></i>'
      : '<i class="fas fa-exclamation-triangle"></i>';

    return `<div class="attendance-timeline-row">
      <div class="attendance-timeline-track">
        <div class="attendance-timeline-segment ${cssClass}"
             style="left:${segment.start_percent}%; width:${segment.width_percent}%;"
             title="${segment.label}"></div>
        <span class="attendance-timeline-label attendance-timeline-label--${cssClass}" style="left:${labelLeft}%;">
          ${icon} ${segment.label}
        </span>
      </div>
    </div>`;
  }).join('');
}

function sessionStatusLabel(session) {
  if (session.complete) {
    return '<span class="session-status-complete"><i class="fas fa-check-circle"></i> Completed</span>';
  }

  if (session.completeness === 'missing_check_out' || !session.check_out_display) {
    return '<span class="session-status-incomplete"><i class="fas fa-exclamation-triangle"></i> Missing checkout</span>';
  }

  return '<span class="session-status-incomplete"><i class="fas fa-exclamation-triangle"></i> Missing check-in</span>';
}

function renderSessionTable(sessions) {
  if (!sessions?.length) {
    return '<p class="text-muted mb-0">No sessions recorded.</p>';
  }

  const rows = sessions.map((session, index) => {
    return `<tr>
      <td>${index + 1}</td>
      <td>${session.check_in_display || '—'}</td>
      <td>${session.check_out_display || '—'}</td>
      <td>${session.worked_duration_display || '—'}</td>
      <td>${sessionStatusLabel(session)}</td>
    </tr>`;
  }).join('');

  return `<table class="attendance-session-table">
    <thead>
      <tr>
        <th>#</th>
        <th>Check-in</th>
        <th>Check-out</th>
        <th>Duration</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>`;
}

function renderTimelineSection(sessions, timeline) {
  if (!sessions?.length) {
    return '';
  }

  return `<div class="attendance-timeline-section">
    <div class="attendance-timeline-title">Timeline</div>
    ${renderTimelineAxis(timeline)}
    ${renderSessionTimelineRows(sessions, timeline)}
  </div>`;
}

function renderSuggestedActions(actions, employeeId, date, permissions) {
  if (!permissions.can_manage || !actions?.length) {
    return '';
  }

  return actions.map((action) => {
    const buttons = [];
    const addLabel = action.add_label || (action.correction_type === 'missing_check_out' ? 'Add checkout' : 'Add check-in');
    const checkType = action.correction_type === 'missing_check_out' ? 'check_out' : 'check_in';
    const suggestedTime = action.suggested_check_out_time || action.suggested_check_in_time;

    buttons.push(`<button type="button" class="attendance-suggested-action__btn attendance-add-punch-btn"
      data-employee-id="${employeeId}" data-date="${date}"
      data-check-type="${checkType}" data-suggested-time="${suggestedTime || ''}">
      ${addLabel}
    </button>`);

    if (action.correction_type === 'missing_check_out') {
      buttons.push(`<button type="button" class="attendance-suggested-action__btn attendance-suggested-action__btn--link attendance-use-checkout"
        data-employee-id="${employeeId}" data-date="${date}"
        data-check-in-id="${action.check_in_id}" data-check-out-time="${action.suggested_check_out_time}">
        ${action.use_label}
      </button>`);
      buttons.push(`<button type="button" class="attendance-suggested-action__btn attendance-suggested-action__btn--danger-link attendance-void-punch"
        data-employee-id="${employeeId}" data-date="${date}" data-punch-id="${action.check_in_id}">
        Delete check-in
      </button>`);
    } else {
      buttons.push(`<button type="button" class="attendance-suggested-action__btn attendance-suggested-action__btn--link attendance-use-checkin"
        data-employee-id="${employeeId}" data-date="${date}"
        data-check-out-id="${action.check_out_id}" data-check-in-time="${action.suggested_check_in_time}">
        ${action.use_label}
      </button>`);
      buttons.push(`<button type="button" class="attendance-suggested-action__btn attendance-suggested-action__btn--danger-link attendance-void-punch"
        data-employee-id="${employeeId}" data-date="${date}" data-punch-id="${action.check_out_id}">
        Delete check-out
      </button>`);
    }

    return `<div class="attendance-suggested-action">
      <div class="attendance-suggested-action__message">${action.message}</div>
      <div class="attendance-suggested-action__buttons">${buttons.join('')}</div>
    </div>`;
  }).join('');
}

function renderDetailsPanel(payload, employeeId, date) {
  const summary = payload.summary || {};
  const permissions = payload.permissions || { can_manage: false };
  const sessions = payload.sessions || [];
  const incompleteCount = summary.incomplete_session_count || 0;

  const footerIncomplete = incompleteCount > 0
    ? `<span class="attendance-details-panel__footer-incomplete">${incompleteCount} incomplete session${incompleteCount === 1 ? '' : 's'}</span>`
    : '';

  return `<div class="attendance-details-panel__header">
    <div class="attendance-details-panel__title">Sessions for ${formatDisplayDate(date)}</div>
    <button type="button" class="attendance-details-panel__collapse attendance-details-collapse-btn"
      data-employee-id="${employeeId}" data-date="${date}" aria-label="Collapse">
      <i class="fas fa-chevron-up"></i>
    </button>
  </div>
  <div class="attendance-details-panel__body">
    <div class="attendance-details-panel__sessions">
      ${renderSessionTable(sessions)}
    </div>
    ${renderTimelineSection(sessions, payload.timeline)}
  </div>
  ${renderSuggestedActions(payload.suggested_actions, employeeId, date, permissions)}
  <div class="attendance-details-panel__footer">
    <span>Total confirmed: <strong>${summary.worked_duration_label || summary.confirmed_duration_label || '0h 00m'}</strong></span>
    ${footerIncomplete}
  </div>`;
}

function refreshDayCell(employeeId, date, payload) {
  const cell = document.querySelector(`.attendance-day-cell[data-employee-id="${employeeId}"][data-date="${date}"]`);
  if (!cell || !payload.summary) return;

  const summary = payload.summary;
  const cardStatus = summary.card_status;

  cell.classList.remove(
    'attendance-day-card--attended',
    'attendance-day-card--incomplete',
    'attendance-day-card--empty'
  );

  if (cardStatus === 'complete' || cardStatus === 'attended') {
    cell.classList.add('attendance-day-card--attended');
  } else if (cardStatus === 'incomplete') {
    cell.classList.add('attendance-day-card--incomplete');
  } else {
    cell.classList.add('attendance-day-card--empty');
  }

  const statusWrapper = cell.querySelector('.attendance-day-card__status');
  const statusEl = statusWrapper?.querySelector('span:last-child');
  if (statusEl) {
    statusEl.textContent = (cardStatus === 'complete' || cardStatus === 'attended')
      ? 'Attended'
      : (cardStatus === 'incomplete' ? 'Incomplete' : 'No Record');
  }

  const checkEl = cell.querySelector('.attendance-day-card__check');
  const warningEl = cell.querySelector('.attendance-day-card__status .fa-exclamation-triangle');
  if (cardStatus === 'complete' || cardStatus === 'attended') {
    if (!checkEl && statusWrapper) {
      const icon = document.createElement('span');
      icon.className = 'attendance-day-card__check';
      icon.textContent = '✓';
      statusWrapper.insertBefore(icon, statusWrapper.firstChild);
    }
    if (warningEl) warningEl.remove();
  } else if (cardStatus === 'incomplete') {
    if (checkEl) checkEl.remove();
    if (!warningEl && statusWrapper) {
      const icon = document.createElement('i');
      icon.className = 'fas fa-exclamation-triangle';
      statusWrapper.insertBefore(icon, statusWrapper.firstChild);
    }
  }

  if (statusWrapper) {
    statusWrapper.classList.remove(
      'attendance-day-card__status--attended',
      'attendance-day-card__status--incomplete',
      'attendance-day-card__status--empty'
    );
    const statusClass = (cardStatus === 'complete' || cardStatus === 'attended')
      ? 'attendance-day-card__status--attended'
      : (cardStatus === 'incomplete' ? 'attendance-day-card__status--incomplete' : 'attendance-day-card__status--empty');
    statusWrapper.classList.add(statusClass);
  }

  const timeEl = cell.querySelector('.attendance-day-card__time');
  if (timeEl) {
    const first = payload.summary.first_check_in_display || payload.summary.first_official_check_in_display || '';
    const last = payload.summary.last_check_out_display || payload.summary.last_official_check_out_display || '';
    timeEl.textContent = first || last ? `${first} → ${last}` : '';
  }

  const metaEl = cell.querySelector('.attendance-day-card__meta');
  if (metaEl) {
    const count = summary.session_count || 0;
    const sessionLabel = count === 1 ? 'session' : 'sessions';
    if (summary.worked_duration_label && summary.worked_duration_label !== '0h 00m' && count > 0) {
      metaEl.textContent = `${summary.worked_duration_label} · ${count} ${sessionLabel}`;
    } else if (summary.confirmed_duration_label && summary.confirmed_duration_label !== '0h 00m' && count > 0) {
      metaEl.textContent = `${summary.confirmed_duration_label} · ${count} ${sessionLabel}`;
    } else if (count > 0) {
      metaEl.textContent = `${count} ${sessionLabel}`;
    } else {
      metaEl.textContent = '';
    }
  }
}

function collapseDetails(employeeId, date) {
  const rowId = `attendance-details-row-${employeeId}-${date}`;
  const expandRow = document.getElementById(rowId);
  const button = document.querySelector(`.attendance-details-toggle-btn[data-employee-id="${employeeId}"][data-date="${date}"]`);
  const card = document.querySelector(`.attendance-day-cell[data-employee-id="${employeeId}"][data-date="${date}"]`);

  if (expandRow) expandRow.classList.add('d-none');
  if (button) button.setAttribute('aria-expanded', 'false');
  if (card) card.classList.remove('is-expanded');
}

function collapseEmployeeDetails(employeeId, exceptDate) {
  document.querySelectorAll(`.attendance-details-expand-row[data-employee-id="${employeeId}"]`).forEach((row) => {
    if (row.dataset.date !== exceptDate) {
      collapseDetails(employeeId, row.dataset.date);
    }
  });
}

function loadPunchDetails(employeeId, date, container) {
  container.innerHTML = '<div class="attendance-details-loading"><i class="fas fa-spinner fa-spin"></i> Loading...</div>';

  fetch(`/attendances/punch_details?employee_id=${employeeId}&date=${date}`, {
    headers: { Accept: 'application/json' }
  })
    .then((response) => response.json())
    .then((payload) => {
      container.innerHTML = renderDetailsPanel(payload, employeeId, date);
      bindDetailsPanelEvents(container);
    })
    .catch((error) => {
      container.innerHTML = '<div class="text-danger p-3">Failed to load details.</div>';
      console.error(error);
    });
}

function postCorrection(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': attendanceCsrfToken()
    },
    body: JSON.stringify(body)
  }).then((response) => {
    if (!response.ok) {
      return response.json().then((data) => Promise.reject(data));
    }
    return response.json();
  });
}

function deleteVoid(url, body) {
  return fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': attendanceCsrfToken()
    },
    body: JSON.stringify(body)
  }).then((response) => {
    if (!response.ok) {
      return response.json().then((data) => Promise.reject(data));
    }
    return response.json();
  });
}

function refreshDetailsPanel(container, employeeId, date, payload) {
  container.innerHTML = renderDetailsPanel(payload, employeeId, date);
  bindDetailsPanelEvents(container);
  refreshDayCell(employeeId, date, payload);
}

function toDatetimeLocalValue(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function bindDetailsPanelEvents(container) {
  container.querySelectorAll('.attendance-use-checkout').forEach((button) => {
    button.addEventListener('click', () => {
      postCorrection('/attendances/copy_as_checkout', {
        employee_id: button.dataset.employeeId,
        check_in_id: button.dataset.checkInId,
        check_out_time: button.dataset.checkOutTime
      }).then((payload) => {
        refreshDetailsPanel(container, button.dataset.employeeId, button.dataset.date, payload);
      }).catch((error) => alert(error.error || 'Correction failed'));
    });
  });

  container.querySelectorAll('.attendance-use-checkin').forEach((button) => {
    button.addEventListener('click', () => {
      postCorrection('/attendances/complete_check_in', {
        employee_id: button.dataset.employeeId,
        check_out_id: button.dataset.checkOutId,
        check_in_time: button.dataset.checkInTime
      }).then((payload) => {
        refreshDetailsPanel(container, button.dataset.employeeId, button.dataset.date, payload);
      }).catch((error) => alert(error.error || 'Correction failed'));
    });
  });

  container.querySelectorAll('.attendance-void-punch').forEach((button) => {
    button.addEventListener('click', () => {
      if (!window.confirm('Void this punch?')) return;

      deleteVoid('/attendances/void_punch', {
        employee_id: button.dataset.employeeId,
        punch_id: button.dataset.punchId
      }).then((payload) => {
        refreshDetailsPanel(container, button.dataset.employeeId, button.dataset.date, payload);
      }).catch((error) => alert(error.error || 'Void failed'));
    });
  });

  container.querySelectorAll('.attendance-add-punch-btn').forEach((button) => {
    button.addEventListener('click', () => {
      const modal = document.getElementById('attendanceCorrectionModal');
      if (!modal) return;

      modal.querySelector('#correctionEmployeeId').value = button.dataset.employeeId;
      modal.querySelector('#correctionDate').value = button.dataset.date;

      const checkTypeSelect = modal.querySelector('#correctionCheckType');
      if (checkTypeSelect && button.dataset.checkType) {
        checkTypeSelect.value = button.dataset.checkType;
      }

      const checkTimeInput = modal.querySelector('#correctionCheckTime');
      if (checkTimeInput && button.dataset.suggestedTime) {
        checkTimeInput.value = toDatetimeLocalValue(button.dataset.suggestedTime);
      }

      window.bootstrap.Modal.getOrCreateInstance(modal).show();
    });
  });

  container.querySelectorAll('.attendance-details-collapse-btn').forEach((button) => {
    button.addEventListener('click', () => {
      collapseDetails(button.dataset.employeeId, button.dataset.date);
    });
  });
}

function toggleDetailsPanel(button) {
  const employeeId = button.dataset.employeeId;
  const date = button.dataset.date;
  const rowId = button.dataset.detailsRowId;
  const expandRow = document.getElementById(rowId);
  const panel = document.getElementById(`attendance-details-panel-${employeeId}-${date}`);
  const card = document.querySelector(`.attendance-day-cell[data-employee-id="${employeeId}"][data-date="${date}"]`);

  if (!expandRow || !panel) return;

  const isHidden = expandRow.classList.contains('d-none');

  collapseEmployeeDetails(employeeId, isHidden ? date : null);

  if (isHidden) {
    expandRow.classList.remove('d-none');
    button.setAttribute('aria-expanded', 'true');
    if (card) card.classList.add('is-expanded');

    if (!panel.dataset.loaded) {
      loadPunchDetails(employeeId, date, panel);
      panel.dataset.loaded = 'true';
    }
  } else {
    collapseDetails(employeeId, date);
  }
}

function bindAttendanceDetails(root = document) {
  root.querySelectorAll('.attendance-details-toggle-btn').forEach((button) => {
    if (button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggleDetailsPanel(button);
    });
  });

  root.querySelectorAll('.attendance-report-card[data-report-url]').forEach((card) => {
    if (card.dataset.reportBound === 'true') return;
    card.dataset.reportBound = 'true';

    const openReportModal = () => {
      fetch(card.dataset.reportUrl, {
        headers: {
          Accept: 'text/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load report form (${response.status})`);
          }
          return response.text();
        })
        .then((script) => {
          // Rails UJS-style script response; executes change_attendance.js.erb
          // eslint-disable-next-line no-eval
          eval(script);
        })
        .catch((error) => {
          console.error(error);
          alert('Failed to open report form.');
        });
    };

    card.addEventListener('click', (event) => {
      if (event.target.closest('a, button, input, select, textarea, form')) return;
      openReportModal();
    });

    card.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openReportModal();
    });
  });
}

function bindCorrectionModal() {
  const form = document.getElementById('attendanceCorrectionForm');
  if (!form || form.dataset.bound === 'true') return;
  form.dataset.bound = 'true';

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const employeeId = form.querySelector('#correctionEmployeeId').value;
    const date = form.querySelector('#correctionDate').value;
    const checkType = form.querySelector('#correctionCheckType').value;
    const checkTime = form.querySelector('#correctionCheckTime').value;

    postCorrection('/attendances/add_punch', {
      employee_id: employeeId,
      check_type: checkType,
      check_time: checkTime
    }).then((payload) => {
      const modal = document.getElementById('attendanceCorrectionModal');
      if (modal) {
        window.bootstrap.Modal.getOrCreateInstance(modal).hide();
      }

      const panel = document.getElementById(`attendance-details-panel-${employeeId}-${date}`);
      if (panel) {
        panel.dataset.loaded = 'true';
        refreshDetailsPanel(panel, employeeId, date, payload);

        const expandRow = document.getElementById(`attendance-details-row-${employeeId}-${date}`);
        if (expandRow) expandRow.classList.remove('d-none');
      } else {
        refreshDayCell(employeeId, date, payload);
      }

      form.reset();
    }).catch((error) => alert(error.error || 'Failed to add punch'));
  });
}

document.addEventListener('DOMContentLoaded', () => {
  bindAttendanceDetails();
  bindCorrectionModal();
});

document.addEventListener('turbolinks:load', () => {
  bindAttendanceDetails();
  bindCorrectionModal();
});

window.bindAttendanceDetails = bindAttendanceDetails;
window.bindCorrectionModal = bindCorrectionModal;

export { bindAttendanceDetails, bindCorrectionModal };
